import { useState, useEffect, useCallback, useRef } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { organizationalUnitsApi } from '../../services/apiService';
import { treeToNodesEdges, collectAllIds } from './orgChartUtils';
import type { TreeNode, TreeResponse } from './orgChartUtils';

interface UseOrgChartDataOptions {
  organizationId?: number;
  organizationName?: string;
  onNodeClick: (node: TreeNode) => void;
  /** Optional set of unit IDs the current user can drill into. Nodes
   *  outside this set render as out-of-scope (gray, non-clickable). */
  inScopeIds?: Set<number>;
}

export function useOrgChartData({ organizationId, organizationName, onNodeClick, inScopeIds }: UseOrgChartDataOptions) {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#2563EB');
  const [secondaryColor, setSecondaryColor] = useState('#A6813F');
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<number>>(new Set());
  const treeDataRef = useRef<TreeNode[]>([]);
  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;

  const toggleExpand = useCallback((nodeId: number) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const buildNodesEdges = useCallback(
    (tree: TreeNode[], expanded: Set<number>, pColor: string, sColor: string) => {
      // The backend tree already includes the real DHR root unit when
      // structure_only mode is on, so we render the tree as-is. Wrapping it
      // in a synthetic root labelled with the user's section name (legacy
      // behaviour) would produce a duplicate-looking top node.
      const processedTree = tree;

      const { nodes: n, edges: e } = treeToNodesEdges(processedTree, {
        primaryColor: pColor,
        secondaryColor: sColor,
        expandedNodeIds: expanded,
        onToggleExpand: toggleExpand,
        onNodeClick: (node) => onNodeClickRef.current(node),
        inScopeIds,
      });
      setNodes(n);
      setEdges(e);
    },
    [organizationName, toggleExpand, inScopeIds],
  );

  const fetchTree = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Always fetch the full DHR hierarchy. Backend returns every unit when
      // `structure_only=true` regardless of the caller's department; the
      // frontend caller can then color-code which parts of the tree are
      // actually inside the user's drill-in scope.
      const response: TreeResponse = await organizationalUnitsApi.tree({
        structure_only: true,
        ...(organizationId ? { organization: organizationId } : {}),
      });
      const tree = response.tree;
      const pColor = response.primary_color || '#2563EB';
      const sColor = response.secondary_color || '#A6813F';

      setPrimaryColor(pColor);
      setSecondaryColor(sColor);
      treeDataRef.current = tree;

      // Expand all nodes by default. We deliberately skip the legacy
      // organization-name wrapper here: when the backend returns the full
      // structure (structure_only mode), `tree` already contains the real
      // DHR root, so wrapping it would render a duplicate top node with
      // the caller's section name above the actual DHR root.
      const allIds = new Set(collectAllIds(tree));
      setExpandedNodeIds(allIds);

      buildNodesEdges(tree, allIds, pColor, sColor);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load org chart';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, organizationName, buildNodesEdges]);

  // Re-build nodes/edges when expandedNodeIds changes
  useEffect(() => {
    if (treeDataRef.current.length > 0) {
      buildNodesEdges(treeDataRef.current, expandedNodeIds, primaryColor, secondaryColor);
    }
  }, [expandedNodeIds, buildNodesEdges, primaryColor, secondaryColor]);

  // Initial fetch
  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  return {
    nodes,
    edges,
    loading,
    error,
    primaryColor,
    secondaryColor,
    refreshTree: fetchTree,
    toggleExpand,
  };
}
